import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getOrgLocationToolLinks,
  postOrgDeleteLocation,
  putOrgLocationToolLinks,
} from "@/lib/adsAssistantClient";
import {
  buildMergedState,
  clearOrgToolLinksLocal,
  getEffectiveLinkTypeMetadata,
  newCustomLinkTypeId,
  type OrgToolLinksStored,
} from "@/lib/locationToolLinks";
import {
  draftWithBaseUrl,
  draftWithLinkTypeMeta,
  draftWithNewLinkType,
  draftWithoutLinkType,
  draftWithOverride,
} from "@/lib/locationToolsDraftOps";
import type { LocationToolsMode } from "./locationToolsContentConstants";

type Props = {
  orgId: string | null;
  locations: { id: string; name: string }[];
  onLocationsUpdated?: () => void;
};

export function useLocationToolsContentModel({ orgId, locations, onLocationsUpdated }: Props) {
  // Slice 14: the "Add a new location" mode now mounts OrgLocationsSection,
  // which owns its own draft state + write path. This view-model used to
  // expose addLocName / addLocWorking / addLocMsg / submitNewLocation for
  // the deleted LocationToolsAddLocationSection; those fields are gone.
  // `onLocationsUpdated` is still wired through deleteSelectedLocation
  // below and is also passed straight through to OrgLocationsSection so
  // the host panel's location dropdown refetches after any mutation.
  const auth = useAuth();
  const [mode, setMode] = useState<LocationToolsMode>("linkTypes");
  const [draft, setDraft] = useState<OrgToolLinksStored>(() => buildMergedState(undefined, null));
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [newTypeDescription, setNewTypeDescription] = useState("");
  const [deleteLocWorking, setDeleteLocWorking] = useState(false);

  const canDeleteLinkType =
    auth.status === "ready" && auth.hasRole("admin", "owner", "platform_admin", "platform_dev");
  const canDeleteLocation = canDeleteLinkType;

  const load = useCallback(async () => {
    if (!orgId) {
      setDraft(buildMergedState(undefined, null));
      setLoadError(null);
      setCanEdit(true);
      return;
    }
    setLoading(true);
    setLoadError(null);
    const r = await getOrgLocationToolLinks();
    setLoading(false);
    if (!r.ok) {
      setLoadError(r.error);
      setDraft(buildMergedState(undefined, orgId));
      setCanEdit(false);
      return;
    }
    const body = r.data as { payload?: OrgToolLinksStored | null; can_edit?: boolean };
    const apiPayload = body?.payload;
    setDraft(buildMergedState(apiPayload ?? null, orgId));
    setCanEdit(body?.can_edit !== false);
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkMetas = useMemo(() => getEffectiveLinkTypeMetadata(draft), [draft]);
  const locOptions = useMemo(() => [...locations].sort((a, b) => a.name.localeCompare(b.name)), [locations]);

  const persist = useCallback(async () => {
    if (!orgId) return;
    setSavedMsg(null);
    const r = await putOrgLocationToolLinks(draft);
    if (!r.ok) {
      setSavedMsg(r.error);
      return;
    }
    clearOrgToolLinksLocal(orgId);
    setSavedMsg("Saved to your organization.");
    window.dispatchEvent(new CustomEvent("bb-org-tool-links-saved", { detail: { orgId } }));
    window.setTimeout(() => setSavedMsg(null), 3200);
    void load();
  }, [draft, orgId, load]);

  const saveDraftImmediate = useCallback(
    async (payload: OrgToolLinksStored) => {
      if (!orgId) return;
      setSavedMsg(null);
      const r = await putOrgLocationToolLinks(payload);
      if (!r.ok) {
        setSavedMsg(r.error);
        return;
      }
      clearOrgToolLinksLocal(orgId);
      setSavedMsg("Saved to your organization.");
      window.dispatchEvent(new CustomEvent("bb-org-tool-links-saved", { detail: { orgId } }));
      window.setTimeout(() => setSavedMsg(null), 3200);
      void load();
    },
    [orgId, load]
  );

  function updateBase(linkId: string, baseUrl: string) {
    setDraft((prev) => draftWithBaseUrl(prev, linkId, baseUrl));
  }

  function setOverride(linkId: string, locationId: string, url: string) {
    setDraft((prev) => draftWithOverride(prev, linkId, locationId, url));
  }

  function updateLinkTypeMeta(linkId: string, label: string, description: string) {
    setDraft((prev) => draftWithLinkTypeMeta(prev, linkId, label, description));
  }

  function addLinkType() {
    const label = newTypeLabel.trim();
    if (!label) return;
    const id = newCustomLinkTypeId();
    const description = newTypeDescription.trim() || "Custom tool link.";
    setDraft((prev) => draftWithNewLinkType(prev, id, label, description));
    setNewTypeLabel("");
    setNewTypeDescription("");
    setShowAddTypeModal(false);
    setSelectedLinkId(id);
  }

  function deleteLinkType(linkId: string) {
    if (!canDeleteLinkType) return;
    const ok = window.confirm(
      "Delete this link type for your organization? This requires org admin approval and removes saved URLs for this type.\n\nContinue?"
    );
    if (!ok) return;
    setDraft((prev) => {
      const next = draftWithoutLinkType(prev, linkId);
      queueMicrotask(() => void saveDraftImmediate(next));
      return next;
    });
    setSelectedLinkId(null);
  }

  async function deleteSelectedLocation() {
    if (!orgId || !selectedLocationId || !canDeleteLocation) return;
    const loc = locOptions.find((l) => l.id === selectedLocationId);
    const label = loc?.name ?? "this location";
    if (
      !window.confirm(
        `Permanently delete “${label}” from your organization? Per-location link overrides for this lot will be removed. Other data that references this location may block deletion — you will see an error if so.\n\nThis cannot be undone. Continue?`
      )
    ) {
      return;
    }
    setDeleteLocWorking(true);
    setSavedMsg(null);
    try {
      const r = await postOrgDeleteLocation(selectedLocationId);
      if (!r.ok) {
        setSavedMsg(r.error);
        return;
      }
      setSelectedLocationId(null);
      clearOrgToolLinksLocal(orgId);
      setSavedMsg("Location removed.");
      window.dispatchEvent(new CustomEvent("bb-org-tool-links-saved", { detail: { orgId } }));
      window.setTimeout(() => setSavedMsg(null), 4200);
      void load();
      onLocationsUpdated?.();
    } finally {
      setDeleteLocWorking(false);
    }
  }

  const selectedMeta = selectedLinkId ? linkMetas.find((m) => m.id === selectedLinkId) : null;
  const selectedLoc = selectedLocationId ? locOptions.find((l) => l.id === selectedLocationId) : null;

  return {
    orgId,
    mode,
    setMode,
    draft,
    savedMsg,
    loadError,
    canEdit,
    loading,
    selectedLinkId,
    setSelectedLinkId,
    selectedLocationId,
    setSelectedLocationId,
    showAddTypeModal,
    setShowAddTypeModal,
    newTypeLabel,
    setNewTypeLabel,
    newTypeDescription,
    setNewTypeDescription,
    deleteLocWorking,
    canDeleteLinkType,
    canDeleteLocation,
    load,
    persist,
    updateBase,
    setOverride,
    updateLinkTypeMeta,
    addLinkType,
    deleteLinkType,
    deleteSelectedLocation,
    linkMetas,
    locOptions,
    selectedMeta,
    selectedLoc,
  };
}

export type LocationToolsContentViewModel = ReturnType<typeof useLocationToolsContentModel>;
